/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import Select, { ActionMeta, SingleValue } from 'react-select'
import styled from 'styled-components'

import erc20DefaultLogo from '../../assets/erc20-default.webp'
import { TOKEN_MAP } from '../../constants'
import { pxToVw } from '../../functions/units'
import { ChainId, CouldBe, Nullable, Token } from '../../types/main'
import Avatar from '../Avatar'
import FlexBox from '../Common/FlexBox'

type TokenOptionProps = Pick<Token, 'name' | 'symbol'> & {
  logoUri: Nullable<string>
}

const TokenOption = ({ logoUri, name, symbol }: TokenOptionProps) => {
  return (
    <FlexBox alignItems="center" columnGap={7}>
      <StyledTokenLogo
        size={pxToVw(26)}
        image={logoUri ?? erc20DefaultLogo}
        circular={false}
        transparentBase
        altText={`${symbol} logo`}
      />
      <StyledTokenLabel>
        <strong>{symbol}</strong> – {name}
      </StyledTokenLabel>
    </FlexBox>
  )
}

export type Props = {
  chainId: CouldBe<number>
  onChange: (
    selectedOption: SingleValue<{ value: string; label: JSX.Element }>,
    actionMeta: ActionMeta<{ value: string; label: JSX.Element }>
  ) => void
}

const TokenSelect = ({ chainId, onChange }: Props) => {
  const options: { value: string; label: JSX.Element }[] = !chainId
    ? []
    : TOKEN_MAP[chainId as ChainId].map((token) => ({
        value: token.address,
        label: <TokenOption logoUri={token.logoUri} name={token.name} symbol={token.symbol} />
      }))

  return (
    <Select menuPlacement="auto" menuPosition="absolute" options={options} onChange={onChange} />
  )
}

export default TokenSelect

const StyledTokenLogo = styled(Avatar)``

const StyledTokenLabel = styled.span`
  color: ${({ theme }) => theme.colors.gray300};

  > strong {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray600};
  }
`
